import { useState, useEffect, useCallback } from 'react';
import { coreApi } from '@shared/api/client';
import { AuditEntry } from '../types';

const INITIAL_FALLBACK_AUDIT: AuditEntry[] = [
  {
    id: 'aud-1',
    actor: 'admin@helios.local',
    action: 'TENANT_INITIALIZE',
    target_entity: 'tenant',
    target_id: '00000000-0000-0000-0000-000000000001',
    timestamp: new Date(Date.now() - 3600000 * 4).toISOString(),
    hash: 'e892c814ef26a7e02df35d1f89312b918732df36070a24128f11883faee23bc4',
    prev_hash: '0000000000000000000000000000000000000000000000000000000000000000',
    ip: '127.0.0.1',
    diff: { status: 'initialized', plan: 'enterprise' },
  },
  {
    id: 'aud-2',
    actor: 'system:seed',
    action: 'CREATE',
    target_entity: 'company',
    target_id: 'comp-101',
    timestamp: new Date(Date.now() - 3600000 * 2).toISOString(),
    hash: '57a840e60882ef9ff7f23a650cbef7a0774a80e72251a248a0dd3e8bcff72839',
    prev_hash: 'e892c814ef26a7e02df35d1f89312b918732df36070a24128f11883faee23bc4',
    ip: '127.0.0.1',
    diff: { name: 'Acme Corporation', domain: 'acme.com' },
  },
];

export function useAuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAudit = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await coreApi.get<Record<string, unknown>[]>('/audit');
      if (Array.isArray(data) && data.length > 0) {
        const normalized: AuditEntry[] = data.map((raw, idx) => ({
          id: String(raw.id ?? `aud-gen-${idx}`),
          timestamp: String(raw.timestamp ?? raw.created_at ?? new Date().toISOString()),
          actor: String(raw.actor ?? raw.actor_id ?? 'system'),
          action: String(raw.action ?? 'UPDATE').toUpperCase(),
          target_entity: String(raw.target_entity ?? raw.resource_type ?? 'system'),
          target_id: String(raw.target_id ?? raw.resource_id ?? ''),
          hash: String(raw.hash ?? ''),
          prev_hash: String(raw.prev_hash ?? ''),
          diff: (raw.diff && typeof raw.diff === 'object' ? raw.diff : {}) as any,
        }));
        setEntries(normalized);
      } else {
        setEntries(INITIAL_FALLBACK_AUDIT);
      }
    } catch {
      setEntries(INITIAL_FALLBACK_AUDIT);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  return { entries, isLoading, error, reload: fetchAudit };
}
